import { Injectable } from '@angular/core';

Injectable({
  providedIn: 'root',
});
export class ImageService {
  maxWidth = 1024; // Maksimum genişlik
  maxHeight = 1024; // Maksimum yükseklik
  maxFileSize = 256 * 1024; // 256KB
  maxRetries = 19; // Maksimum deneme sayısı
  qualityStep = 0.07; // Kalite adım boyutu (biraz daha agresif)
  minQuality = 0.2; // Minimum kalite

  /**
   * fileType: 'profile' | 'passport' | 'other' | 'pdf'
   */
  async resizeImage(file: File, fileType: 'profile' | 'passport' | 'other' | 'pdf' = 'other'): Promise<File> {
    // PDF ise hiç dokunma
    if (file.type === 'application/pdf' || fileType === 'pdf') {
      return file;
    }
    let maxFileSize = 256 * 1024; // default
    if (fileType === 'profile') maxFileSize = 50 * 1024;
    if (fileType === 'passport') maxFileSize = 500 * 1024;
    // Eğer dosya PNG/JPEG ve zaten küçükse hiç dokunma
    if ((file.type === 'image/png' || file.type === 'image/jpeg') && file.size <= maxFileSize) {
      return file;
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Resim ebatlarını koruyarak yeniden boyutlandır
        const aspectRatio = img.width / img.height;
        let width = img.width;
        let height = img.height;

        if (width > this.maxWidth) {
          width = this.maxWidth;
          height = width / aspectRatio;
        }
        if (height > this.maxHeight) {
          height = this.maxHeight;
          width = height * aspectRatio;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        // PNG ise JPEG'e çevirerek kaliteyi uygula
        let outType = file.type;
        let startQuality = 1;
        if (file.type === 'image/png' || file.type === 'image/webp') {
          outType = 'image/jpeg';
          startQuality = 0.7; // PNG'den JPEG'e çevirirken kaliteyi 0.7'den başlat
        }

        try {
          const compressedBlob = await this.compressImage(canvas, outType, startQuality, this.maxRetries, maxFileSize);
          // Eğer sıkıştırılmış dosya orijinalden büyükse orijinali döndür
          if (compressedBlob.size > file.size) {
            resolve(file);
            return;
          }
          if (compressedBlob.size > maxFileSize) {
            reject(new Error('Görsel istenen boyuta sıkıştırılamadı. Lütfen daha küçük bir görsel seçin.'));
            return;
          }
          const resizedFile = new File([compressedBlob], file.name, {
            type: outType,
          });
          resolve(resizedFile);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = (error) => reject(error);
    });
  }

  compressImage(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number,
    retries: number,
    maxFileSize: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (quality < this.minQuality) {
        reject(new Error('Minimum kaliteye ulaşıldı, daha fazla sıkıştırılamaz.'));
        return;
      }
      canvas.toBlob(
        (blob) => {
          if (blob) {
            if (blob.size <= maxFileSize || retries <= 0) {
              resolve(blob);
            } else {
              // Kaliteyi azalt ve tekrar dene
              this.compressImage(
                canvas,
                type,
                quality - this.qualityStep,
                retries - 1,
                maxFileSize
              )
                .then(resolve)
                .catch(reject);
            }
          } else {
            reject(new Error('Resim sıkıştırma işlemi başarısız oldu.'));
          }
        },
        type,
        quality
      );
    });
  }
  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
